/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState } from "react";

import styled from "styled-components";

import { useCurrentUser } from "src/app/useCurrentUser";
import { BaseUser } from "src/classes/user";
import { FollowButton } from "src/components/Profile/FollowButton";
import { StandardButton } from "src/components/UI/Buttons/StandardButton";
import { safeSegmentTrack } from "src/lib/logging/segment";

import AvatarAndName from "./AvatarAndName";
import EditProfile from "./EditProfile";

interface Props {
  user: BaseUser;
}
export function UserInfo({ user }: Props) {
  const currentUser = useCurrentUser();
  const [isEditing, setIsEditing] = useState(false);

  const isSelf = user.snapshot.id === currentUser?.snapshot.id;

  const handleEdit = () => {
    setIsEditing(true);
    safeSegmentTrack({ event: "Edit Profile Modal Opened" });
  };

  return (
    <>
      {isEditing && <EditProfile setEditModalOpen={setIsEditing} />}
      <Wrapper>
        <Actions>
          <AvatarAndName user={user} />
          {currentUser &&
            (isSelf ? (
              <StandardButton onClick={handleEdit}>Edit Profile</StandardButton>
            ) : (
              <FollowButton user={user} />
            ))}
        </Actions>
        {user.snapshot.bio && (
          <Bio>
            {" >"} {user.snapshot.bio}
          </Bio>
        )}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-start;
  font-family: "Inter";
  color: ${({ theme }) => theme.text.default};
`;

const Actions = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const Bio = styled.div`
  font-family: "IBM Plex Mono";
  font-size: 14px;
  font-weight: 500;
  margin-top: 10px;
`;
